import type { ClosingWizardFilePreview } from '../components/closing-setup-wizard/closing-setup-wizard.types';
import type {
    IAccountingFilePreview,
    IAccountingFileProblem,
    IAccountingFileRefusal,
} from '../models/closing-setup.model';

/**
 * REV-22007 — the accounting file's server answers, as the lines the two pure views render.
 *
 * ONE presenter for both screens that show an upload — the setup wizard's panel and the workspace's
 * re-import dialog — so a refusal reads the same in both places. It holds no vocabulary: the caller
 * passes its translate function, the keys come from the server (`abcp.fileImport.*`).
 */
export type AccountingFileTranslate = (key: string, params?: Readonly<Record<string, unknown>>) => string;

/** Two decimals, Swiss grouping — a difference of `0` reads `0.00`, which is the point of showing it. */
const AMOUNT = new Intl.NumberFormat('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** "Zeile 12: Kontonummer 1020 kommt doppelt vor." — or the bare message for a file-level problem. */
export function accountingFileProblemLine(problem: IAccountingFileProblem, t: AccountingFileTranslate): string {
    const message = t(problem.messageKey, problem.params ?? {});

    return problem.row === null ? message : t('abcp.fileImport.row', { row: problem.row, message });
}

/**
 * The refusal as lines: the lead first, then every row under it.
 *
 * A file-level refusal (unbalanced, a missing column) arrives with its lead repeated as the only row;
 * that duplicate is dropped, or the box would say the same sentence twice. A refusal made of ROW
 * problems gets a counting lead instead of its first row's message — the rows below already say it.
 */
export function accountingFileRefusalLines(refusal: IAccountingFileRefusal | null, t: AccountingFileTranslate): string[] {
    if (refusal === null) {
        return [];
    }

    const rowLevel = refusal.rows.length > 0 && refusal.rows.every((row) => row.row !== null);
    const lead = rowLevel
        ? t('abcp.fileImport.refusedRows', { count: refusal.rows.length })
        : t(refusal.messageKey, refusal.params ?? {});
    const rows = refusal.rows
        .filter((row) => !(row.row === null && row.messageKey === refusal.messageKey))
        .map((row) => accountingFileProblemLine(row, t));

    return [lead, ...rows];
}

/** The preview card: file name, account count, the formatted difference, the translated warnings. */
export function accountingFilePreviewOf(preview: IAccountingFilePreview | null, t: AccountingFileTranslate): ClosingWizardFilePreview | null {
    if (preview === null) {
        return null;
    }

    return {
        fileName: preview.fileName,
        accounts: preview.accounts,
        difference: AMOUNT.format(preview.difference),
        warnings: preview.warnings.map((warning) => accountingFileProblemLine(warning, t)),
    };
}
